/**
 * Client for the `billing` edge function. No component reads a billing table
 * or calls a Stripe function itself: the wallet, its ledger, the plan, the
 * last failed reload and every payment go through here.
 *
 * Any member may read; only an owner or admin may write (the function answers
 * 403 otherwise). A refusal the screens act on carries its code in the
 * envelope's `data` (`MANDATE_REQUIRED`, `RETRY_SAME_REQUEST`, `USE_CHECKOUT`…),
 * which every call here hands on as `BillingError.code`. Keep in sync with
 * `supabase/functions/billing/`.
 */

import { api, ApiError } from "@/lib/api-client";
import { UNREACHABLE } from "@/utils/refusal";

const route = (workspaceId: string, path: string) => `/billing/${workspaceId}/${path}`;

/**
 * The workspace's money, as `GET billing/:id/wallet` answers it. Named as the
 * `workspaces` columns are, without a Stripe id: `payment_method_added` holds
 * only while a card is stored, `stripe_mandate_status` is null without a
 * mandate, and `auto_reload_pending` says a reload the bank is holding is on
 * its way.
 */
export interface WorkspaceBilling {
  id: string;
  name: string;
  wallet_balance_credits: number;
  free_minutes_remaining: number;
  free_minutes_total_used: number;
  auto_reload_enabled: boolean;
  auto_reload_threshold_credits: number;
  auto_reload_amount_credits: number;
  max_daily_spend_credits: number | null;
  daily_spend_credits: number;
  is_billing_paused: boolean;
  payment_method_added: boolean;
  usage_rate_per_minute_override: number | null;
  /** Server-enforced cap on simultaneous calls; the source of truth for concurrency. */
  call_concurrency_limit: number | null;
  /** ISO country of the saved card's issuer. Only the literal "IN" changes behaviour. */
  payment_method_country: string | null;
  payment_method_brand: string | null;
  /** The RBI e-mandate cards issued in India reload under. Null without one. */
  stripe_mandate_status: "pending" | "active" | "inactive" | null;
  auto_reload_mandate_amount_credits: number | null;
  /** A reload the bank is holding (~26 h) — it lands on its own and cannot be cancelled. */
  auto_reload_pending: boolean;
  auto_reload_pending_completes_at: string | null;
  auto_reload_pending_amount_credits: number | null;
  auto_reload_pending_approval_requested: boolean | null;
  low_balance_alerted_at: string | null;
}

/**
 * One payment, as `POST billing/:id/payments` answers it: usage rows are left
 * out, `metadata` holds only `reason`, `reason_code` and `attempted_amount`,
 * and an adjustment comes without its description, which is the operator's note.
 */
export interface WorkspaceBillingTransaction {
  id: string;
  transaction_type: 'top_up' | 'usage' | 'refund' | 'auto_reload' | 'adjustment' | 'subscription_charge' | 'free_minute_usage' | 'transfer' | 'top_up_failed' | 'subscription_refund';
  /** Signed: negative for refunds and debit adjustments. May carry 4 decimals. */
  amount_credits: number;
  balance_before_credits: number;
  balance_after_credits: number;
  description: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** The workspace's latest plan, whatever its status. */
export interface WorkspaceSubscription {
  /** active | trialing | past_due | canceled | unpaid | expired | … */
  status: string;
  current_period_start: string;
  current_period_end: string;
  canceled_at: string | null;
  ended_at: string | null;
  concurrency_limit: number;
  campaign_access: boolean;
}

/** The last reload that failed, while none has gone through since. */
export interface AutoReloadFailureAnswer {
  reason: string;
  occurred_at: string;
  /** This failure switched auto-reload off, rather than being a strike still counting. */
  disabled: boolean;
  decline_code: string | null;
  /** Failures in a row so far ("attempt N of 3"). */
  attempt: number | null;
}

/** The trigger an Indian card's reloads should start at, from the last week's spend. */
export interface SpendAnswer {
  has_data: boolean;
  daily_spend: number;
  suggested_threshold: number;
}

export interface CheckoutStatus {
  payment_status: "paid" | "unpaid" | "no_payment_required";
  credited: boolean;
  amount: number;
  currency: string;
  balance_after: number | null;
}

/** A saved-card top-up (contract C6). `pending`: charged, with the webhook still writing the credit. */
export interface TopUpAnswer {
  status: "credited" | "pending";
  already_processed: boolean;
  balance_before: number | null;
  balance_after: number | null;
}

export interface AutoReloadSettingsInput {
  auto_reload_enabled?: boolean;
  auto_reload_threshold_credits?: number;
  auto_reload_amount_credits?: number;
}

export interface MandateSetupAnswer {
  required: boolean;
  country?: string | null;
  mandate_status?: "pending" | "active" | null;
  client_secret?: string;
  setup_intent_id?: string;
  payment_method_id?: string;
  publishable_key?: string;
  cap_usd?: number;
}

export interface SubscribeAnswer {
  status: "requires_confirmation" | "active" | "trialing" | "processing";
  subscription_id?: string;
  client_secret?: string;
  payment_method_id?: string;
  publishable_key?: string;
}

/** A removed card. A reload already announced to the bank still lands after it (it cannot be cancelled). */
export interface CardRemovalAnswer {
  pending_reload: { amount: number; completes_at: string | null } | null;
}

/**
 * A billing call's failure: the server's sentence, its machine-readable `code`
 * and the HTTP status — 0 when no answer came back at all (offline, a
 * timeout), so the request may or may not have run. A top-up's also says
 * whether it was the saved-card charge (`charge`) rather than a Checkout link.
 */
export type BillingError = Error & { code?: string | null; status?: number; charge?: boolean };

/** Every call here: the answer, or a `BillingError`. */
async function billing<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (!(error instanceof ApiError)) throw Object.assign(new Error(UNREACHABLE), { code: null, status: 0 });
    const code = (error.data as { code?: unknown } | undefined)?.code;
    throw Object.assign(new Error(error.message), { code: typeof code === "string" ? code : null, status: error.status });
  }
}

export const getWallet = (workspaceId: string) =>
  billing(api.getItem<WorkspaceBilling>(route(workspaceId, "wallet")));

/** Newest first. `page` counts from 1. */
export const listPayments = (workspaceId: string, page: number, size: number) =>
  billing(api.postPage<WorkspaceBillingTransaction>(route(workspaceId, "payments"), { page, size, search: "" }));

/** Null when the workspace never had a plan — an answer, not an error. */
export const getSubscription = (workspaceId: string) =>
  billing(api.getItemOrNull<WorkspaceSubscription>(route(workspaceId, "subscription")));

/** Null when no reload has failed since the last that went through. */
export const getAutoReloadFailure = (workspaceId: string) =>
  billing(api.getItemOrNull<AutoReloadFailureAnswer>(route(workspaceId, "auto-reload/failure")));

export const getSpend = (workspaceId: string) =>
  billing(api.getItem<SpendAnswer>(route(workspaceId, "auto-reload/spend")));

export const getCheckoutStatus = (workspaceId: string, sessionId: string) =>
  billing(api.getItem<CheckoutStatus>(route(workspaceId, `checkout/${sessionId}`)));

/** `requestId` is one attempt: its retry reuses it, so Stripe answers with the charge it already made. */
export const topUp = (workspaceId: string, amount: number, requestId: string) =>
  billing(api.post<TopUpAnswer>(route(workspaceId, "top-up"), { amount, request_id: requestId }));

/** A Stripe Checkout link for `amount`, which also saves the card it is paid with. */
export const createCheckout = (workspaceId: string, amount: number) =>
  billing(api.post<{ checkout_url: string }>(route(workspaceId, "checkout"), { amount }));

export const saveAutoReload = (workspaceId: string, settings: AutoReloadSettingsInput) =>
  billing(api.put<unknown>(route(workspaceId, "auto-reload"), settings));

/** No `setupIntentId`: start the bank authorisation. With one: record what the bank confirmed. */
export const setUpMandate = (workspaceId: string, setupIntentId?: string) =>
  billing(api.post<MandateSetupAnswer>(route(workspaceId, "auto-reload/mandate"), setupIntentId ? { setup_intent_id: setupIntentId } : {}));

/** `requestId` is one upgrade attempt (contract C7): retrying it is the same subscription in Stripe. */
export const startSubscription = (workspaceId: string, requestId: string) =>
  billing(api.post<SubscribeAnswer>(route(workspaceId, "subscription"), { request_id: requestId }));

export const confirmSubscription = (workspaceId: string, subscriptionId: string) =>
  billing(api.post<SubscribeAnswer>(route(workspaceId, "subscription/confirm"), { subscription_id: subscriptionId }));

export const portalLink = (workspaceId: string) =>
  billing(api.post<{ url: string }>(route(workspaceId, "portal"), {}));

export const removePaymentMethod = (workspaceId: string) =>
  billing(api.delItem<CardRemovalAnswer>(route(workspaceId, "payment-method")));
